import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { parseISO, format } from 'date-fns';
import moment from 'moment';

import Loading from "../../components/Loading";
import Meta from "../../components/Meta";
import Header from "../../components/Header";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

const STATUS_OPTIONS = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
const PAYMENT_STATUS_OPTIONS = ['pending', 'paid', 'deposit_paid', 'refunded'];

const formatPrice = (price) => price === 0 ? 'Free' : `£${(price / 100).toFixed(2)}`;

const downloadBlob = (data, filename) => {
	const blob = new Blob([data], { type: 'text/csv' });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();
	link.parentNode.removeChild(link);
	window.URL.revokeObjectURL(url);
}

function Report() {
	const { id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(true);
	const [exporting, setExporting] = useState(false);
	const [error, setError] = useState(null);
	const [report, setReport] = useState(null);

	const [filters, setFilters] = useState({
		start: searchParams.get('start') || moment().startOf('month').format('YYYY-MM-DD'),
		end: searchParams.get('end') || moment().endOf('month').format('YYYY-MM-DD'),
		status: searchParams.get('status') ? searchParams.get('status').split(',') : [],
		paymentStatus: searchParams.get('paymentStatus') ? searchParams.get('paymentStatus').split(',') : ['paid', 'deposit_paid']
	});

	const fetchReport = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await Data.fetchStaffReport(id, {
				start: filters.start,
				end: filters.end,
				status: filters.status.join(','),
				paymentStatus: filters.paymentStatus.join(',')
			});
			setReport(res.data);
		} catch (err) {
			console.error('Error fetching staff report:', err);
			setError(err.response?.data?.message || 'Failed to load report');
		} finally {
			setLoading(false);
		}
	}, [id, filters]);

	useEffect(() => {
		setSearchParams({
			start: filters.start,
			end: filters.end,
			status: filters.status.join(','),
			paymentStatus: filters.paymentStatus.join(',')
		});
		fetchReport();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters]);

	const handleDateChange = (key, value) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	};

	const toggleCheckbox = (key, value) => {
		setFilters((prev) => {
			const current = prev[key];
			const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
			return { ...prev, [key]: next };
		});
	};

	const handleExport = async () => {
		setExporting(true);
		try {
			const res = await Data.exportStaffReport(id, {
				start: filters.start,
				end: filters.end,
				status: filters.status.join(','),
				paymentStatus: filters.paymentStatus.join(',')
			});
			const staffName = report?.staff ? `${report.staff.firstName}-${report.staff.lastName}` : id;
			downloadBlob(res.data, `${staffName}-report.csv`);
		} catch (err) {
			console.error('Error exporting staff report:', err);
		} finally {
			setExporting(false);
		}
	};

	return (
		<>
			<Meta title="Staff Report" />
			<Header
				heading={report?.staff ? `${report.staff.firstName} ${report.staff.lastName} - Report` : 'Staff Report'}
				button={<Link to="/admin/staff" className="btn">Back to Staff</Link>}
			/>
			<main>
				<div className={styles.withFilters}>
					<div className={styles.filters}>
						<div>
							<label>Start Date</label>
							<DatePicker
								value={filters.start ? parseISO(filters.start) : null}
								onChange={(date) => handleDateChange('start', date ? format(date, 'yyyy-MM-dd') : '')}
							/>
						</div>
						<div>
							<label>End Date</label>
							<DatePicker
								value={filters.end ? parseISO(filters.end) : null}
								onChange={(date) => handleDateChange('end', date ? format(date, 'yyyy-MM-dd') : '')}
							/>
						</div>
						<div>
							<label>Status</label>
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
								{STATUS_OPTIONS.map((option) => (
									<label key={option} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
										<input
											type="checkbox"
											style={{ width: 'auto', padding: 0 }}
											checked={filters.status.includes(option)}
											onChange={() => toggleCheckbox('status', option)}
										/>
										{option}
									</label>
								))}
							</div>
							<p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0' }}>Leave unchecked to include all statuses.</p>
						</div>
						<div>
							<label>Payment Status</label>
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
								{PAYMENT_STATUS_OPTIONS.map((option) => (
									<label key={option} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal' }}>
										<input
											type="checkbox"
											style={{ width: 'auto', padding: 0 }}
											checked={filters.paymentStatus.includes(option)}
											onChange={() => toggleCheckbox('paymentStatus', option)}
										/>
										{option}
									</label>
								))}
							</div>
							<p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0' }}>Leave unchecked to include all payment statuses.</p>
						</div>
						<div style={{ display: 'flex', alignItems: 'flex-end' }}>
							<button type="button" className="btn btnPrimary" onClick={handleExport} disabled={exporting || loading}>
								{exporting ? 'Exporting...' : 'Export CSV'}
							</button>
						</div>
					</div>

					{loading ? (
						<Loading button={false} />
					) : error ? (
						<div><p>{error}</p></div>
					) : (
						<>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
								<p><strong>{report.count}</strong> booking{report.count === 1 ? '' : 's'}</p>
								<p style={{ fontSize: '18px' }}>Total: <strong>{formatPrice(report.total)}</strong></p>
							</div>

							<div style={{ overflowX: 'auto' }}>
								<table style={{ width: '100%', borderCollapse: 'collapse' }}>
									<thead>
										<tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
											<th style={{ padding: '8px' }}>Date</th>
											<th style={{ padding: '8px' }}>Time</th>
											<th style={{ padding: '8px' }}>Customer</th>
											<th style={{ padding: '8px' }}>Service</th>
											<th style={{ padding: '8px' }}>Status</th>
											<th style={{ padding: '8px' }}>Payment</th>
											<th style={{ padding: '8px' }}>Reference</th>
											<th style={{ padding: '8px', textAlign: 'right' }}>Amount</th>
										</tr>
									</thead>
									<tbody>
										{!report.bookings.length && (
											<tr><td colSpan={8} style={{ padding: '16px', textAlign: 'center', color: '#888' }}>No bookings found for the selected filters.</td></tr>
										)}
										{report.bookings.map((booking) => (
											<tr key={booking._id} style={{ borderBottom: '1px solid #f0f0f0' }}>
												<td style={{ padding: '8px' }}>{moment(booking.date).format('ddd, MMM D, YYYY')}</td>
												<td style={{ padding: '8px' }}>{booking.startTime} - {booking.endTime}</td>
												<td style={{ padding: '8px' }}>{booking.User ? `${booking.User.firstName} ${booking.User.lastName}` : '-'}</td>
												<td style={{ padding: '8px' }}>{booking.Service?.name || '-'}</td>
												<td style={{ padding: '8px', textTransform: 'capitalize' }}>{booking.status}</td>
												<td style={{ padding: '8px', textTransform: 'capitalize' }}>{booking.paymentStatus?.replace('_', ' ')}</td>
												<td style={{ padding: '8px' }}>{booking.reference}</td>
												<td style={{ padding: '8px', textAlign: 'right' }}>{formatPrice(booking.price)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</>
					)}
				</div>
			</main>
		</>
	);
}

export default Report;
